import {isValid, parseISO} from 'date-fns'
import {
  DynamicVariableSwitch,
  DynamicVariableScript,
  ListVariable,
  ListVariableValue,
  SimpleVariable,
  SimpleVariableValue,
  StatementCase,
  StaticVariable,
  Variable,
  VariableValue
} from '../../../common/types/variable-engine'
import {
  getProfileFieldName,
  validProfileVarName
} from '../../../common/variable-engine'
import {removeFromArray, updateArray} from '../../../lib/updaters'
import {VariableAction} from './actions'
import * as t from 'io-ts'

export type VariableTypes =
  | 'string'
  | 'list'
  | 'date'
  | 'number'
  | 'boolean'
  | 'switch'
  | 'script'

export type FormState = {
  /** Root toggle 'mode' */
  mode: 'custom' | 'user-profile'

  /** name for non-profile var
   * separate field to retain when toggling between modes */
  name: string
  /** used as variable name but on save is prepended like: 'profile.city' */
  userProfileSnapFieldName: string

  /** current variable type */
  activeVarType: VariableTypes

  // the variable data itself (chosen by 'activeVarType')
  string: SimpleVariable
  date: SimpleVariable
  number: SimpleVariable
  boolean: SimpleVariable
  list: ListVariable
  script: DynamicVariableScript

  switch: DynamicVariableSwitch
  conditionIndex?: number
  newCondition: boolean

  // generic form state
  dirty: boolean
}

export type NonSwitchVariableType =
  | Exclude<StaticVariable['type'], undefined>
  | 'script'
export const ProfileSnapshotFieldTypes = t.union([
  t.literal('string'),
  t.literal('date')
])
export type ValidProfileSnapshotFieldTypes = t.TypeOf<
  typeof ProfileSnapshotFieldTypes
>

export const initVariableState = ({
  name,
  variable
}: {
  name: string
  variable: Variable
}): FormState => {
  const mode = name.match(validProfileVarName) ? 'user-profile' : 'custom'
  const trimmedProfileName =
    mode === 'user-profile' && getProfileFieldName(name)

  const emptySwitch: DynamicVariableSwitch = {
    source: 'dynamic',
    type: 'switch',
    statement: {
      cases: []
    },
    default: Array.isArray(variable.default)
      ? undefined
      : (variable.default as string)
  }
  const base: FormState = {
    mode,
    name: mode === 'custom' ? name : '',
    userProfileSnapFieldName: trimmedProfileName ? trimmedProfileName : '',
    activeVarType: 'string', // default
    switch: emptySwitch,
    script: {type: 'script', script: ''},
    number: {type: 'number'},
    string: {type: 'string'},
    boolean: {type: 'boolean'},
    date: {type: 'date'},
    list: {type: 'list', default: []} as ListVariable,
    dirty: false,
    newCondition: false
  }

  if (isSwitchVariable(variable)) {
    return {
      ...base,
      activeVarType: 'switch',
      switch: variable
    }
  }
  if (isListVariable(variable)) {
    return {
      ...base,
      activeVarType: 'list',
      list: variable
    }
  }
  const inferredType = inferSimpleType(
    variable.default
  ) as SimpleVariable['type']
  const type = variable.type ?? inferredType ?? 'string'
  return {
    ...base,
    // @ts-ignore - script not handled yet, will be addressed with UI PR
    activeVarType: type,
    [type]: variable
  }
}

export const variableReducer = (
  state: FormState,
  action: VariableAction
): FormState => {
  switch (action.type) {
    case 'setRootMode': {
      const restrictedType = ProfileSnapshotFieldTypes.is(state.activeVarType)
        ? state.activeVarType
        : 'string'

      return {
        ...state,
        // need to reset it otherwise fieldname might not match the type
        userProfileSnapFieldName: '',
        mode: action.payload,
        activeVarType: restrictedType,
        dirty: true
      }
    }
    case 'setCustomName':
      return {
        ...state,
        name: action.payload,
        dirty: true
      }
    case 'setProfileFieldName':
      return {
        ...state,
        userProfileSnapFieldName: action.payload.name,
        activeVarType: action.payload.type,
        dirty: true
      }
    case 'setRuntime':
      return {
        ...state,
        dirty: true,
        string: {
          ...state.string,
          runtime: action.payload
        },
        switch: {
          ...state.switch,
          runtime: action.payload
        }
      }
    case 'setType': {
      return {
        ...state,
        activeVarType:
          action.payload === 'string' && state.switch.statement.cases.length
            ? 'switch'
            : action.payload,
        dirty: true,
        // On selection of boolean explicity set value to false,
        // rather than relying on falsey undefined value as default.
        ...(action.payload === 'boolean'
          ? {boolean: {default: false, ...state.boolean}}
          : {})
      }
    }
    case 'setDefaultValue': {
      switch (state.activeVarType) {
        case 'list':
          return state
        case 'string':
        case 'switch':
          return {
            ...state,
            string: {
              ...state.string,
              default:
                action.payload !== undefined
                  ? convertValue(action.payload, 'string') || ''
                  : undefined
            },
            switch: {
              ...state.switch,
              default:
                action.payload !== undefined
                  ? (convertValue(action.payload, 'string') as string) || ''
                  : undefined
            },
            dirty: true
          }
        case 'boolean':
        case 'date':
        case 'number':
          return {
            ...state,
            [state.activeVarType]: {
              ...state[state.activeVarType],
              default:
                action.payload !== undefined
                  ? convertValue(action.payload, state.activeVarType) ?? ''
                  : undefined
            },
            dirty: true
          }
        default:
          return state
      }
    }
    case 'setListDefault': {
      const safeValue = action.payload
        ? convertValue(action.payload ?? state.list.default, 'list')
        : undefined
      return safeValue === undefined
        ? state
        : {
            ...state,
            list: {
              ...state.list,
              default: safeValue
            },
            dirty: true
          }
    }
    case 'setNonWrappingFormatter':
      return {
        ...state,
        string: {
          ...state.string,
          defaultFormatter: action.payload.formatter,
          defaultFormatterOption: undefined
        },
        switch: {
          ...state.switch,
          defaultFormatter: action.payload.formatter
        },
        dirty: true
      }
    case 'setDateFormatter':
      return {
        ...state,
        date: {
          ...state.date,
          defaultFormatter: action.payload.formatter,
          defaultFormatterOption: action.payload.formatterOption
        },
        dirty: true
      }
    case 'setAllowEventOverride': {
      const stateEventOverride =
        state.activeVarType !== 'switch' && state.activeVarType !== 'script'
          ? state[state.activeVarType].eventOverride
          : undefined
      if (!stateEventOverride) return state
      return {
        ...state,
        [state.activeVarType]: {
          ...state[state.activeVarType],
          eventOverride: {
            ...stateEventOverride,
            disabled: action.payload
          }
        },
        dirty: true
      }
    }
    case 'setAllowEventOverrideRequired': {
      if (state.activeVarType === 'switch' || state.activeVarType === 'script')
        return state
      const stateEventOverride = state[state.activeVarType].eventOverride
      return {
        ...state,
        [state.activeVarType]: {
          ...state[state.activeVarType],
          eventOverride: {
            ...stateEventOverride,
            disabled: action.payload ? false : stateEventOverride?.disabled,
            required: action.payload
          }
        },
        dirty: true
      }
    }
    case 'setConditionIndex': {
      const cancellingNewCondition =
        state.newCondition && action.payload === undefined
      return cancellingNewCondition
        ? {
            ...state,
            switch: {
              ...state.switch,
              statement: {
                ...state.switch.statement,
                cases: removeFromArray(
                  state.switch.statement.cases,
                  state.switch.statement.cases.length
                )
              }
            },
            conditionIndex: undefined,
            newCondition: false,
            dirty: true
          }
        : {
            ...state,
            conditionIndex: action.payload,
            newCondition: false
          }
    }
    case 'setStatementCase':
      return {
        ...state,
        switch: {
          ...state.switch,
          statement: {
            ...state.switch.statement,
            cases: updateArray(
              state.switch.statement.cases,
              action.payload.index,
              action.payload.statementCase
            )
          }
        },
        conditionIndex: undefined,
        dirty: true
      }
    case 'addCondition': {
      const newIndex = state.switch.statement.cases.length
      return {
        ...state,
        activeVarType: 'switch',
        switch: {
          ...state.switch,
          statement: {
            ...state.switch.statement,
            cases: state.switch.statement.cases.concat({
              comparison: {
                type: 'equals',
                left: {type: 'static', value: ''},
                right: {type: 'static', value: ''}
              },
              expression: ''
            })
          }
        },
        newCondition: true,
        conditionIndex: newIndex,
        dirty: true
      }
    }

    case 'deleteCondition': {
      const removeIndex = state.conditionIndex
      if (removeIndex === undefined) return state
      const removedCase = removeFromArray(
        state.switch.statement.cases,
        removeIndex
      )

      return {
        ...state,
        activeVarType:
          removedCase.length === 0 ? 'string' : state.activeVarType,
        switch: {
          ...state.switch,
          statement: {
            ...state.switch.statement,
            cases: removedCase
          }
        },
        conditionIndex: undefined,
        newCondition: false,
        dirty: true
      }
    }

    case 'updateStatementCase': {
      const existing = state.switch.statement.cases[action.payload.index]
      const updated: StatementCase = {
        ...existing,
        ...action.payload.statementCase,
        comparison: {
          ...existing.comparison,
          ...action.payload.statementCase.comparison
        }
      }
      return {
        ...state,
        switch: {
          ...state.switch,
          statement: {
            ...state.switch.statement,
            cases: updateArray(
              state.switch.statement.cases,
              action.payload.index,
              updated
            )
          }
        },
        conditionIndex: undefined,
        dirty: true
      }
    }

    case 'removeStatementCase': {
      const removedCase = removeFromArray(
        state.switch.statement.cases,
        action.payload.index
      )

      return {
        ...state,
        activeVarType:
          removedCase.length === 0 ? 'string' : state.activeVarType,
        switch: {
          ...state.switch,
          statement: {
            ...state.switch.statement,
            cases: removedCase
          }
        },
        dirty: true
      }
    }

    case 'reset':
      return initVariableState({
        name: action.name,
        variable: action.variable
      })

    case 'setScript':
      return {
        ...state,
        dirty: true,
        script: {
          ...state.script,
          script: action.payload
        }
      }

    default: {
      const exhaustiveCheck: never = action
      throw new Error(`Unhandled step type: ${exhaustiveCheck}`)
    }
  }
}

export function convertValue(
  value: SimpleVariable['default'],
  type: SimpleVariable['type']
): SimpleVariableValue
export function convertValue(
  value: ListVariable['default'],
  type: ListVariable['type']
): ListVariableValue
export function convertValue(
  value: StaticVariable['default'],
  type: StaticVariable['type']
): VariableValue | undefined {
  value = typeof value === 'object' && 'value' in value ? value.value : value
  const valueType = typeof value

  if (valueType === type) {
    return value
  }

  switch (type) {
    case 'list': {
      if (Array.isArray(value)) {
        return value
      }
      return String(value).split(',')
    }
    case 'boolean':
      return !!value
    case 'number': {
      const match = String(value).match(/[0-9]*(:?\.[0-9]*)?/g)
      if (match) {
        return match.length === 1 ? match[0] : match.filter(Boolean).join('') // handles case where existing value was 123abc then user typed 4 => 1234
      }
      return undefined
    }
    case 'date': {
      value = String(value)
      if (isValid(parseISO(value))) {
        return value
      }
      return undefined
    }
    case 'string':
      return String(value)
    default:
      return value
  }
}

const isListVariable = (variable: Variable): variable is ListVariable => {
  return (
    variable.type === 'list' ||
    (!!variable.default && inferSimpleType(variable.default) === 'list')
  )
}

const isSwitchVariable = (
  variable: Variable
): variable is DynamicVariableSwitch => {
  return variable.type === 'switch'
}

function inferSimpleType(
  value: Variable['default']
): Variable['type'] | undefined {
  if (!value) {
    return
  }

  if (value && typeof value === 'object' && 'value' in value) {
    return inferSimpleType(value.value)
  }

  if (Array.isArray(value)) {
    return 'list'
  }

  if (typeof value === 'string' && isValid(parseISO(value))) {
    return 'date'
  }

  const type = typeof value

  if (type === 'string' || type === 'number' || type === 'boolean') {
    return type
  }

  return undefined
}
